import React from "react";
import { motion, useInView } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { fadeUpVariants, reducedMotionVariants } from "@/lib/animations";

export interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  /** Animation delay in seconds */
  delay?: number;
  /** Viewport threshold to trigger animation (0-1) */
  threshold?: number;
  /** Whether to trigger animation only once */
  once?: boolean;
}

/**
 * AnimatedSection - 滚动触发的区块动画组件
 *
 * 当元素进入视口时触发淡入上升动画
 * 支持 prefers-reduced-motion 无障碍降级
 *
 * @example
 * ```tsx
 * <AnimatedSection delay={0.2}>
 *   <h2>Section Title</h2>
 *   <p>Section content...</p>
 * </AnimatedSection>
 * ```
 */
export function AnimatedSection({
  children,
  className,
  delay = 0,
  threshold = 0.2,
  once = true,
}: AnimatedSectionProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once,
    amount: threshold,
  });
  const prefersReducedMotion = useReducedMotion();

  // 无障碍降级：直接渲染无动画
  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeUpVariants}
      custom={delay}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default AnimatedSection;
